import { useEffect, useState } from "react";
import { CheckCircle2, CircleAlert, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const eur = (v) => `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const Check = ({ ok, label, id }) => (
  <li className="flex items-center gap-2 text-sm" data-testid={`stripe-check-${id}`}>
    {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <CircleAlert className="h-4 w-4 text-amber-500" />}
    <span className={ok ? "text-slate-800" : "text-slate-600"}>{label}</span>
  </li>
);

const Stat = ({ label, value, id }) => (
  <div className="rounded-lg border border-slate-200 p-4" data-testid={`stripe-stat-${id}`}>
    <p className="text-2xl font-semibold tabular-nums">{value}</p>
    <p className="text-xs text-slate-500 mt-1">{label}</p>
  </div>
);

export const StripePanel = () => {
  const [s, setS] = useState(null);
  const load = () => { setS(null); api.get("/superadmin/stripe").then(({ data }) => setS(data)).catch((e) => toast.error(errMsg(e))); };
  useEffect(load, []);
  if (!s) return <p className="text-sm text-slate-500">Chargement du compte Stripe…</p>;
  const live = s.mode === "live";
  return (
    <div className="space-y-6 max-w-4xl" data-testid="sa-stripe">
      <section className={`rounded-lg border p-6 ${live ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="stripe-mode-banner">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-lg font-semibold" data-testid="stripe-mode">{live ? "Paiements réels activés" : "Mode test : aucun vrai paiement"}</p>
            <p className="text-sm text-slate-600 mt-1">
              Compte <span className="font-mono">{s.account_id || "—"}</span>{s.name ? ` · ${s.name}` : ""}{s.country ? ` · ${s.country}` : ""}{s.email ? ` · ${s.email}` : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="stripe-refresh-btn"><RefreshCw className="h-4 w-4 mr-1" />Actualiser</Button>
            <Button asChild size="sm"><a href={s.dashboard_url} target="_blank" rel="noreferrer" data-testid="stripe-dashboard-link">Tableau de bord Stripe <ExternalLink className="h-4 w-4 ml-1" /></a></Button>
          </div>
        </div>
        {s.error && <p className="text-sm text-red-600 mt-3">{s.error}</p>}
      </section>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat id="available" label="Solde disponible" value={eur(s.balance.available)} />
        <Stat id="pending" label="En attente de versement" value={eur(s.balance.pending)} />
        <Stat id="subscriptions" label="Abonnements actifs" value={s.subscriptions} />
        <Stat id="customers" label="Clients Stripe" value={s.customers} />
        <Stat id="plans" label="Formules en vente" value={s.plans} />
      </div>

      <section className="rounded-lg border border-slate-200 p-6">
        <h3 className="font-semibold mb-3">État du compte</h3>
        <ul className="space-y-2">
          <Check id="live" ok={live} label={live ? "Clés de production en place" : "Clés de test : les paiements ne sont pas encaissés"} />
          <Check id="charges" ok={s.charges_enabled} label={s.charges_enabled ? "Encaissement des cartes activé" : "Encaissement pas encore activé par Stripe"} />
          <Check id="payouts" ok={s.payouts_enabled} label={s.payouts_enabled ? "Versements vers votre banque activés" : "Versements vers votre banque pas encore activés"} />
          <Check id="details" ok={s.details_submitted} label={s.details_submitted ? "Informations de l'entreprise transmises à Stripe" : "Informations de l'entreprise et pièce d'identité à compléter chez Stripe"} />
          <Check id="webhook" ok={s.webhook} label={s.webhook ? "Notifications Stripe reliées à ScreenView (paiements, impayés)" : "Notifications Stripe non reliées"} />
        </ul>
      </section>

      {!live && (
        <section className="rounded-lg border border-slate-200 p-6" data-testid="stripe-go-live">
          <h3 className="font-semibold mb-3">Recevoir les paiements sur votre compte Stripe</h3>
          <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-700">
            <li>Dans Emergent, cliquez sur <b>Manage</b> (à côté de Preview), puis sur l'onglet <b>Payments</b>.</li>
            <li>Cliquez sur <b>Claim Sandbox</b> et connectez-vous à votre compte Stripe (ou créez-le).</li>
            <li>Complétez la vérification demandée par Stripe (entreprise, IBAN, pièce d'identité).</li>
            <li>Installez l'application <b>Emergent Payments</b> proposée dans Stripe.</li>
            <li>Republiez ScreenView : cette page affichera « Paiements réels activés ».</li>
          </ol>
          <p className="text-xs text-slate-500 mt-3">Vos formules, codes promo et emails restent en place.</p>
        </section>
      )}
    </div>
  );
};
